import BaseAuthAdapter from './BaseAuthAdapter';
import appModeService from 'services/AppModeService';

/**
 * Parent Portal Authentication Adapter
 * 
 * Handles authentication using parent portal token (embedded mode)
 */
class ParentPortalAuthAdapter extends BaseAuthAdapter {
  /**
   * Validate if the user is authenticated
   */
  async validateUser(forceRefresh = false) {
    const token = appModeService.getToken();
    const isTokenReady = appModeService.isTokenReady;
    const error = appModeService.error;

    if (error) {
      return {
        isValidUser: false,
        userInfo: null,
        token: null,
        isEmbedded: true,
        error: error,
      };
    }

    if (isTokenReady && token) {
      return {
        isValidUser: true,
        userInfo: this.extractUserInfoFromToken(token),
        token: token,
        isEmbedded: true,
      };
    }

    return {
      isValidUser: false,
      userInfo: null,
      token: null,
      isEmbedded: true,
    };
  }

  /**
   * Get the current authentication token
   */
  getToken() {
    return appModeService.getToken();
  }

  /**
   * Initiate login flow
   * Not applicable in embedded mode - login is handled by parent
   */
  async login(options = {}) {
    throw new Error('Login not available in embedded mode. Authentication is handled by parent portal.');
  }

  /**
   * Handle logout
   * In embedded mode, we can't logout - parent portal manages session
   */
  async logout() {
    // Clear local token but can't logout from parent portal
    appModeService.clearToken();
    return Promise.resolve();
  }

  /**
   * Get user information
   * Attempts to extract from token or returns null
   */
  async getUserInfo() {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    return this.extractUserInfoFromToken(token);
  }

  /**
   * Check if user is authenticated (synchronous)
   */
  isAuthenticated() {
    const token = appModeService.getToken();
    const isTokenReady = appModeService.isTokenReady;
    return isTokenReady && !!token;
  }

  /**
   * Refresh the authentication token
   * Requests new token from parent portal
   */
  async refreshToken() {
    appModeService.requestToken();
    
    // Wait a bit for token to arrive
    return new Promise((resolve) => {
      const checkToken = () => {
        const token = appModeService.getToken();
        if (token) {
          resolve(token);
        } else {
          setTimeout(checkToken, 100);
        }
      };
      
      // Timeout after 5 seconds
      setTimeout(() => resolve(null), 5000);
      checkToken();
    });
  }

  /**
   * Extract user info from token (if token contains user data)
   * This is a placeholder - implement based on your token structure
   */
  extractUserInfoFromToken(token) {
    try {
      // If token is JWT, decode it
      // This is a simplified example - adjust based on your token format
      if (token && token.includes('.')) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          name: payload.name || payload.sub || null,
          email: payload.email || null,
          // Add other fields as needed
        };
      }
    } catch (error) {
      // Token is not JWT or can't be decoded
      console.warn('Could not extract user info from token:', error);
    }

    return null;
  }
}

export default ParentPortalAuthAdapter;

